import Http from '../Http';

// Cache en memoria para catálogos estáticos y detalle de cliente
let formGeneralesCache: any = null;
let formContactosCache: any = null;
let catalogoOptionsCache: any = null;
let detalleCompletoCache: Record<string, any> = {};
let detalleCompletoPromises: Record<string, Promise<any>> = {};

export class ClienteService {
  static async obtenerLista(
    page = 0,
    limit = 15,
    filters: Record<string, string> = {},
    orderBy = '',
    order = 'asc'
  ) {
    const params = new URLSearchParams();
    params.set('p', page.toString());
    params.set('l', limit.toString());

    if (Object.keys(filters).length) {
      params.set('q', JSON.stringify(filters));
    }
    if (orderBy) {
      params.set('orderBy', orderBy);
      params.set('order', order);
    }

    try {
      const res = await Http.get(`cliente/filter?${params.toString()}`);
      return res.data?.rows || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  static async obtenerPorId(id: string | number) {
    try {
      const res = await Http.get(`cliente/one/${id}`);
      return res.data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  static invalidarDetalleCompleto(id?: string | number) {
    if (id !== undefined && id !== null) {
      delete detalleCompletoCache[String(id)];
      delete detalleCompletoPromises[String(id)];
    } else {
      detalleCompletoCache = {};
      detalleCompletoPromises = {};
    }
  }

  static async obtenerDetalleCompleto(id: string | number, forceRefresh = false) {
    if (!id) return null;
    const key = String(id);

    if (!forceRefresh && detalleCompletoCache[key]) {
      return detalleCompletoCache[key];
    }

    if (!forceRefresh && detalleCompletoPromises[key]) {
      return detalleCompletoPromises[key];
    }

    const fetchPromise = (async () => {
      try {
        const res = await Http.get(`cliente/detalle-completo/${id}`);
        if (res && !res.error && res.data) {
          detalleCompletoCache[key] = res.data;
          return res.data;
        }
        // Fallback a one/:id si el endpoint no responde
        const fallback = await Http.get(`cliente/one/${id}`);
        if (fallback?.data) {
          detalleCompletoCache[key] = fallback.data;
          return fallback.data;
        }
        return null;
      } catch (e) {
        console.error('Error en obtenerDetalleCompleto:', e);
        return null;
      } finally {
        delete detalleCompletoPromises[key];
      }
    })();

    detalleCompletoPromises[key] = fetchPromise;
    return fetchPromise;
  }

  static async obtenerCatalogoOptions() {
    if (catalogoOptionsCache) {
      return catalogoOptionsCache;
    }
    try {
      const res = await Http.get('cliente/catalogo-options');
      if (res && !res.error && res.data) {
        catalogoOptionsCache = res.data;
        return res.data;
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  static async buscarEjecutivos(query: string) {
    try {
      const res = await Http.get(`cliente/form/ventas?e=${encodeURIComponent(query)}`);
      return res.data?.ejecutivos || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  static async guardar(data: any) {
    try {
      const res = await Http.post(`cliente/save`, data);
      if (data?.id) {
        ClienteService.invalidarDetalleCompleto(data.id);
      }
      return res;
    } catch (e) {
      console.error(e);
      return { error: true, errMsg: 'Error al guardar el cliente' };
    }
  }

  static async eliminar(row: any) {
    try {
      const res = await Http.post(`cliente/delete`, row);
      if (row?.id) {
        ClienteService.invalidarDetalleCompleto(row.id);
      }
      return !res.error;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  static async obtenerFormGenerales(params: { cp?: string; e?: string; id_cliente?: string | number } = {}) {
    const isBaseRequest = !params.cp && !params.e && !params.id_cliente;
    if (isBaseRequest && formGeneralesCache) {
      return formGeneralesCache;
    }

    const query = new URLSearchParams();
    if (params.cp) query.set('cp', params.cp);
    if (params.e) query.set('e', params.e);
    if (params.id_cliente) query.set('id_cliente', String(params.id_cliente));

    try {
      const res = await Http.get(`cliente/form-generales?${query.toString()}`);
      if (isBaseRequest && res.data) {
        formGeneralesCache = res.data;
      }
      return res.data || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  static async obtenerFormContactos() {
    if (formContactosCache) {
      return formContactosCache;
    }
    try {
      const res = await Http.get('cliente/form-contactos');
      if (res.data) {
        formContactosCache = res.data;
      }
      return res.data || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  static async obtenerContactos(clienteId: string | number) {
    try {
      const res = await Http.get(`cliente/contactos?c=${clienteId}`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  static async guardarContactos(contactos: any[]) {
    try {
      const res = await Http.post('cliente/contactos/save', { contactos });
      return res;
    } catch (e) {
      console.error(e);
      return { error: true, errMsg: 'Error al guardar contactos' };
    }
  }

  static async eliminarContacto(id: string | number) {
    try {
      const res = await Http.post('cliente/contacto/delete', { id });
      return !res.error;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  static async obtenerRemitentes(
    clienteId: string | number,
    page = 0,
    limit = 15,
    filters: Record<string, string> = {},
    orderBy = '',
    order = 'asc'
  ) {
    const params = new URLSearchParams();
    params.set('p', page.toString());
    params.set('l', limit.toString());
    if (Object.keys(filters).length) {
      params.set('q', JSON.stringify(filters));
    }
    if (orderBy) {
      params.set('orderBy', orderBy);
      params.set('order', order);
    }
    try {
      const res = await Http.get(`cliente-remitente/${clienteId}/filter?${params.toString()}`);
      return {
        rows: res.data?.rows || [],
        total: res.data?.total || 0,
      };
    } catch (e) {
      console.error(e);
      return { rows: [], total: 0 };
    }
  }

  static async eliminarRemitente(row: any) {
    try {
      const res = await Http.post('cliente-remitente/delete', row);
      return !res.error;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  static async obtenerDestinatarios(
    clienteId: string | number,
    page = 0,
    limit = 15,
    filters: Record<string, string> = {},
    orderBy = '',
    order = 'asc'
  ) {
    const params = new URLSearchParams();
    params.set('p', page.toString());
    params.set('l', limit.toString());
    if (Object.keys(filters).length) {
      params.set('q', JSON.stringify(filters));
    }
    if (orderBy) {
      params.set('orderBy', orderBy);
      params.set('order', order);
    }
    try {
      const res = await Http.get(`cliente-destinatario/${clienteId}/filter?${params.toString()}`);
      return {
        rows: res.data?.rows || [],
        total: res.data?.total || 0,
      };
    } catch (e) {
      console.error(e);
      return { rows: [], total: 0 };
    }
  }

  static async eliminarDestinatario(row: any) {
    try {
      const res = await Http.post('cliente-destinatario/delete', row);
      return !res.error;
    } catch (e) {
      console.error(e);
      return false;
    }
  }
}

export default ClienteService;
